<template>
  <Teleport to="body">
    <div v-if="show" class="modal-overlay" @click.self="close">
      <div
        class="modal-content"
        :class="{ 'slides-view': currentStep === 'select' }"
      >
        <!-- Step 1: URL Input -->
        <div v-if="currentStep === 'input'">
          <h3>
            {{ props.isChanging ? "Change Slide Deck" : "Add a Slide Deck" }}
          </h3>
          <div class="modal-body">
            <div class="input-group">
              <label for="slideUrl">Attach a Slide Deck (Google Slides)</label>
              <div class="input-with-button">
                <input
                  type="text"
                  id="slideUrl"
                  v-model="slideUrl"
                  placeholder="https://docs.google.com/presentation/d/..."
                  @input="validateUrl"
                />
              </div>
              <div v-if="urlError" class="error-message">{{ urlError }}</div>
            </div>

            <div v-if="isLoading" class="loading-container">
              <div class="loading-spinner"></div>
              <p>Loading presentation data...</p>
            </div>

            <!-- Error message display -->
            <div v-if="slideError" class="error-container">
              <div class="error-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="8" x2="12" y2="12"></line>
                  <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
              </div>
              <div class="error-content">
                <h4>{{ slideError.title }}</h4>
                <p>{{ slideError.message }}</p>
                <p v-if="slideError.type === 'restricted'" class="error-tip">
                  <strong>Tip:</strong> Make sure your Google Slides
                  presentation is publicly accessible or shared with the
                  appropriate permissions.
                </p>
              </div>
            </div>

            <div
              class="preview-container"
              v-if="isValidUrl && !isLoading && !slideError"
            >
              <h4>Preview</h4>

              <!-- Thumbnail preview -->
              <!-- <div v-if="thumbnailUrl" class="thumbnail-preview">
                <img :src="thumbnailUrl" alt="Slide thumbnail" />
              </div> -->

              <!-- Fallback embed preview if no thumbnail -->
              <div class="slide-preview">
                <iframe
                  :src="embedUrl"
                  frameborder="0"
                  width="100%"
                  height="400"
                  allowfullscreen="true"
                ></iframe>
              </div>

              <div class="slide-info" v-if="slideInfo">
                <p><strong>Slides:</strong> {{ slideInfo.slideCount }}</p>
                <p>
                  <strong>Last updated:</strong>
                  {{ formatDate(slideInfo.lastUpdated) }}
                </p>
              </div>
            </div>
          </div>

          <div class="modal-footer">
            <button class="btn-cancel" @click="close">Cancel</button>
            <button
              class="btn-next"
              @click="goToSelectStep"
              :disabled="!isValidUrl || isLoading"
            >
              Next
            </button>
          </div>
        </div>

        <!-- Step 2: Select Slides and Assign to Tabs -->
        <div v-if="currentStep === 'select'" class="slides-selection-container">
          <h3>Select Slides to Include</h3>

          <div class="slides-layout">
            <!-- Left panel: Slide thumbnails with checkboxes -->
            <div class="slides-list">
              <div class="selection-header">
                <!-- Tab selector -->
                <p class="slide-selection-description">
                  Select which slides you'd like to include under each section
                </p>
                <div class="tab-selector">
                  <div
                    v-for="tab in tabs"
                    :key="tab.value"
                    class="tab-selector-item"
                    :class="{
                      active: selectedTab === normalizeTabValue(tab.value),
                    }"
                    @click="setActiveTab(tab.value)"
                  >
                    {{ tab.name }}
                    <span
                      v-if="
                        selectedSlidesPerTab[normalizeTabValue(tab.value)]
                          ?.length > 0
                      "
                      class="tab-selection-count"
                    >
                      ({{
                        selectedSlidesPerTab[normalizeTabValue(tab.value)]
                          .length
                      }})
                    </span>
                  </div>
                </div>

                <div class="mt-2">
                  <label class="select-all-label">
                    <input
                      type="checkbox"
                      :checked="allSlidesSelected"
                      @change="toggleAllSlides"
                    />
                    <span>Select All </span>
                  </label>
                </div>
              </div>

              <div class="slides-list-content">
                <div
                  v-for="(slide, index) in slides"
                  :key="index"
                  class="slide-item"
                  :class="{
                    selected: isSlideSelected(index),
                    active: activeSlide === index,
                  }"
                  @click="handleSlideItemClick(index)"
                >
                  <div class="slide-item-content">
                    <div class="d-flex ga-3 align-items-start">
                      <div class="slide-checkbox">
                        <input
                          type="checkbox"
                          :checked="isSlideSelected(index)"
                          @click.stop.prevent="handleSlideItemClick(index)"
                        />
                      </div>
                      <div class="slide-number">Slide {{ index + 1 }}</div>
                    </div>
                    <div class="slide-thumbnail">
                      <img
                        :src="slide.thumbnailUrl || placeholderImage"
                        :alt="`Slide ${index + 1}`"
                      />
                    </div>
                  </div>
                  <div
                    class="slide-notes-container"
                    @click.stop="handleSlideItemClick(index)"
                  >
                    <textarea
                      v-model="slides[index].notes"
                      placeholder="No speaker notes for this slide. You can add custom notes here."
                      rows="6"
                      @click.stop
                    ></textarea>
                    <div
                      class="slide-notes-select-gutter"
                      aria-hidden="true"
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="modal-footer">
            <button class="btn-back" @click="currentStep = 'input'">
              Back
            </button>
            <button class="btn-cancel" @click="close">Cancel</button>
            <button
              class="btn-insert"
              @click="insertSelectedSlides"
              :disabled="selectedSlides.length === 0"
            >
              Insert Selected Slides
            </button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import lessonPlanService from "@/services/lessonPlanService";
import professionalDevelopmentService from "@/services/professionalDevelopmentService";
import makercartActivityService from "@/services/makercartActivityService";
import { CATEGORY_TYPES } from "@/utils/constants";
import { computed, defineEmits, defineProps, onMounted, ref, watch } from "vue";

const props = defineProps({
  show: {
    type: Boolean,
    required: true,
  },
  availableTabs: {
    type: Array,
    required: true,
  },
  activeTabIndex: {
    type: Number,
    default: 0,
  },
  isChanging: {
    type: Boolean,
    default: false,
  },
  currentSlideUrl: {
    type: String,
    default: "",
  },
  moduleType: {
    type: String,
    default: "",
    required: true,
    validator: (value) => {
      return [
        CATEGORY_TYPES.LessonPlan,
        CATEGORY_TYPES.ProfessionalDevelopment,
        CATEGORY_TYPES.LessonMaterials,
      ].includes(value);
    },
  },
});

const emit = defineEmits(["close", "insert"]);

// Multi-step tracking
const currentStep = ref("input");

// Slide data
const slideUrl = ref("");
const urlError = ref("");
const isValidUrl = ref(false);
const isLoading = ref(false);
const slideInfo = ref(null);
const thumbnailUrl = ref(null);
const slideError = ref(null);
const placeholderImage =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='150' viewBox='0 0 200 150'%3E%3Crect width='200' height='150' fill='%23f8f9fa'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='sans-serif' font-size='14' fill='%23495057'%3ENo Preview%3C/text%3E%3C/svg%3E";

// Google API state
const gapiInited = ref(false);
const gisInited = ref(false);
let tokenClient = null;

// Slide selection data
const slides = ref([]);
const selectedSlidesPerTab = ref({});
const selectedSlides = ref([]);
const activeSlide = ref(null);
const selectedTab = ref(null);
const slideUploadCode = ref(null);

// API keys and configuration
const API_KEY = process.env.VUE_APP_GOOGLE_API_KEY;
const CLIENT_ID = process.env.VUE_APP_GOOGLE_CLIENT_ID;
const DISCOVERY_DOC =
  "https://slides.googleapis.com/$discovery/rest?version=v1";
const SCOPES = "https://www.googleapis.com/auth/presentations.readonly";

const tabs = ref([{ name: "Default Tab", value: "0" }]);

watch(
  () => props.availableTabs,
  (newTabs) => {
    if (newTabs && newTabs.length > 0) {
      tabs.value = newTabs.map((tab) => ({
        name: tab.name,
        value: normalizeTabValue(tab.value),
      }));

      tabs.value.forEach((tab) => {
        const normalizedTabValue = normalizeTabValue(tab.value);

        if (!selectedSlidesPerTab.value[normalizedTabValue]) {
          selectedSlidesPerTab.value[normalizedTabValue] = [];
        }
      });

      if (selectedTab.value !== null) {
        selectedSlides.value =
          selectedSlidesPerTab.value[selectedTab.value] || [];
      }
    }
  },
  { immediate: true, deep: true }
);

// Make sure the setActiveTab function works properly with the new tabs array
const setActiveTab = (value) => {
  // Normalize value to ensure consistent type
  const normalizedValue = normalizeTabValue(value);

  // Store the current selection for the previous tab
  if (selectedTab.value !== null) {
    selectedSlidesPerTab.value[selectedTab.value] = [...selectedSlides.value];
  }

  // Update the selected tab
  selectedTab.value = normalizedValue;

  selectedSlides.value = selectedSlidesPerTab.value[normalizedValue] || [];
};

function normalizeTabValue(index) {
  return Number(index).toString();
}

// Format date
const formatDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toLocaleDateString();
};

// Computed property to check if all slides are selected
const allSlidesSelected = computed(() => {
  return (
    slides.value.length > 0 &&
    selectedSlides.value.length === slides.value.length
  );
});

// Initialize Google API Client
const initializeGapiClient = async () => {
  try {
    await window.gapi.client.init({
      apiKey: API_KEY,
      discoveryDocs: [DISCOVERY_DOC],
    });
    gapiInited.value = true;
    checkAuthState();
  } catch (error) {
    console.error("Error initializing GAPI client:", error);
  }
};

// Initialize Google Identity Services
const initializeGis = () => {
  try {
    tokenClient = window.google.accounts.oauth2.initTokenClient({
      client_id: CLIENT_ID,
      scope: SCOPES,
      callback: "", // We'll set this dynamically when needed
    });
    gisInited.value = true;
    checkAuthState();
  } catch (error) {
    console.error("Error initializing GIS:", error);
  }
};

// Check if both APIs are initialized
const checkAuthState = () => {
  return gapiInited.value && gisInited.value;
};

// Request authentication from user with a callback
const requestAuth = (callback) => {
  if (!checkAuthState()) {
    console.error("Google APIs not yet initialized");
    return;
  }

  try {
    tokenClient.callback = callback;

    if (window.gapi.client.getToken() === null) {
      // New session - need full consent
      tokenClient.requestAccessToken({ prompt: "consent" });
    } else {
      // Existing session - no prompt needed
      tokenClient.requestAccessToken({ prompt: "" });
    }
  } catch (error) {
    console.error("Error during authentication:", error);
  }
};

// Load Google API Scripts
const loadGoogleApis = () => {
  return new Promise((resolve) => {
    // Check if scripts are already loaded
    if (gapiInited.value && gisInited.value) {
      resolve();
      return;
    }

    // Check if scripts are already in the document
    if (
      document.querySelector(
        'script[src="https://apis.google.com/js/api.js"]'
      ) &&
      document.querySelector(
        'script[src="https://accounts.google.com/gsi/client"]'
      )
    ) {
      // Scripts exist but might not be initialized yet, wait a bit
      setTimeout(() => {
        if (window.gapi && window.google?.accounts) {
          if (!gapiInited.value) {
            window.gapi.load("client", () => {
              initializeGapiClient().then(() => {
                if (!gisInited.value) initializeGis();
                resolve();
              });
            });
          } else {
            resolve();
          }
        } else {
          // Fallback if something went wrong
          console.warn("Google API scripts found but not initialized properly");
          loadScriptsSequentially(resolve);
        }
      }, 500);
      return;
    }

    loadScriptsSequentially(resolve);
  });
};

const loadScriptsSequentially = (resolve) => {
  const scriptGapi = document.createElement("script");
  scriptGapi.src = "https://apis.google.com/js/api.js";
  scriptGapi.onload = () => {
    window.gapi.load("client", () => {
      initializeGapiClient().then(() => {
        if (
          !document.querySelector(
            'script[src="https://accounts.google.com/gsi/client"]'
          )
        ) {
          const scriptGis = document.createElement("script");
          scriptGis.src = "https://accounts.google.com/gsi/client";
          scriptGis.onload = () => {
            initializeGis();
            resolve();
          };
          document.body.appendChild(scriptGis);
        } else {
          initializeGis();
          resolve();
        }
      });
    });
  };
  document.body.appendChild(scriptGapi);
};

// Convert Google Slides URL to embed URL
const embedUrl = computed(() => {
  if (!isValidUrl.value) return "";

  const slideId = getSlideId(slideUrl.value);
  if (slideId) {
    return `https://docs.google.com/presentation/d/${slideId}/embed`;
  }
  return "";
});

const requestThumbnailSlides = async (data) => {
  if (props.moduleType === CATEGORY_TYPES.LessonPlan) {
    return lessonPlanService.requestThumbnailSlides(data);
  } else if (props.moduleType === CATEGORY_TYPES.ProfessionalDevelopment) {
    return professionalDevelopmentService.requestThumbnailSlides(data);
  } else if (props.moduleType === CATEGORY_TYPES.LessonMaterials) {
    return makercartActivityService.requestThumbnailSlides(data);
  }
};

// Get slide ID from URL
const getSlideId = (url) => {
  try {
    const match = url.match(/\/d\/([a-zA-Z0-9_-]+)/);
    return match ? match[1] : null;
  } catch (e) {
    return null;
  }
};

// Fetch slide information and thumbnails
const fetchSlideInfo = async (slideId) => {
  if (!slideId) return;

  isLoading.value = true;
  slideInfo.value = null;
  thumbnailUrl.value = null;
  slides.value = [];
  selectedSlides.value = []; // Reset selected slides
  slideError.value = null; // Reset error state

  try {
    // If we have auth, try to get presentation data from the API
    if (checkAuthState()) {
      const tokenCallback = async (resp) => {
        if (resp.error !== undefined) {
          console.error("Auth error during slide info fetch:", resp);

          // Handle authentication error
          slideError.value = {
            type: "auth",
            title: "Authentication Error",
            message: "Unable to authenticate with Google. Please try again.",
          };

          isLoading.value = false;
          return;
        }

        try {
          // Get presentation data
          const response = await window.gapi.client.slides.presentations.get({
            presentationId: slideId,
          });

          const presentation = response.result;

          if (presentation) {
            // Set slide info
            slideInfo.value = {
              slideCount: presentation.slides ? presentation.slides.length : 0,
              lastUpdated:
                presentation.lastModifiedTime || new Date().toISOString(),
            };

            // Create slides array with thumbnails and notes
            if (presentation.slides && presentation.slides.length > 0) {
              // Populate slides array for the selection step
              const tempSlides = [];
              for (const [index, slide] of presentation.slides.entries()) {
                try {
                  // Get thumbnail for each slide
                  const slideThumbResponse =
                    await window.gapi.client.slides.presentations.pages.getThumbnail(
                      {
                        presentationId: slideId,
                        pageObjectId: slide.objectId,
                        thumbnailProperties: {
                          thumbnailSize: "MEDIUM",
                        },
                      }
                    );

                  // Get slide notes (if available)
                  let notes = "";
                  if (
                    slide.slideProperties &&
                    slide.slideProperties.notesPage
                  ) {
                    const notesPage = slide.slideProperties?.notesPage;
                    if (notesPage) {
                      notes = notesPage.pageElements
                        .map((element) =>
                          element.shape?.text?.textElements
                            ?.map((te) => te.textRun?.content)
                            .filter((content) => content)
                            .join("")
                        )
                        .filter((text) => text)
                        .join("\n");
                    }
                  }

                  tempSlides.push({
                    index: index,
                    objectId: slide.objectId,
                    thumbnailUrl: slideThumbResponse.result.contentUrl,
                    notes: notes,
                    title: `Slide ${index + 1}`,
                  });
                } catch (slideError) {
                  console.error(
                    `Error fetching data for slide ${index + 1}:`,
                    slideError
                  );
                  // Add a placeholder slide even if thumbnail fetch fails
                  tempSlides.push({
                    index: index,
                    objectId: slide.objectId || `slide-${index}`,
                    thumbnailUrl: null,
                    notes: "",
                    title: `Slide ${index + 1}`,
                  });
                }
              }

              const availableSlides = tempSlides.filter(
                (slide) => slide.thumbnailUrl !== null
              );

              if (availableSlides.length === 0) {
                // All thumbnails failed to load - likely a permission issue
                slideError.value = {
                  type: "thumbnails",
                  title: "Unable to Load Thumbnails",
                  message:
                    "Could not load slide thumbnails. This may be due to permission restrictions.",
                };
                isLoading.value = false;
                return;
              }

              let slidesData = availableSlides.map((slide) => ({
                thumbnail: slide.thumbnailUrl,
                slide_number: slide.index,
              }));

              let newSlidesThumbnail = null;
              try {
                const response = await requestThumbnailSlides({
                  slides: slidesData,
                });

                if (response.status === 200) {
                  newSlidesThumbnail = response.data.saved;
                  slideUploadCode.value = response.data.code;
                } else {
                  // Handle non-200 response
                  slideError.value = {
                    type: "api",
                    title: "Server Error",
                    message:
                      "Unable to process slide thumbnails. Please try again later.",
                  };
                  isLoading.value = false;
                  return;
                }
              } catch (error) {
                console.error("Error requesting thumbnail slides:", error);
                slideError.value = {
                  type: "api",
                  title: "API Error",
                  message:
                    "Unable to process slide thumbnails. Please try again later.",
                };
                isLoading.value = false;
                return;
              }

              tempSlides.forEach((slide) => {
                const slideData = newSlidesThumbnail.find(
                  (s) => s.slide_number === slide.index
                );
                if (slideData) {
                  slide.thumbnailUrl = slideData.url;
                }

                return slide;
              });

              // Set slides and select all by default
              slides.value = tempSlides;
              thumbnailUrl.value = tempSlides[0].thumbnailUrl;
              selectedSlides.value = tempSlides.map((_, i) => i);
              activeSlide.value = 0;
            } else {
              // No slides in the presentation
              slideError.value = {
                type: "empty",
                title: "No Slides Found",
                message: "This presentation does not contain any slides.",
              };
            }
          }
        } catch (apiError) {
          console.error("API error:", apiError);

          // Check for specific error types
          if (
            apiError.status === 403 ||
            (apiError.result &&
              apiError.result.error &&
              apiError.result.error.status === "PERMISSION_DENIED")
          ) {
            slideError.value = {
              type: "restricted",
              title: "Access Restricted",
              message:
                "You don't have permission to access this presentation. It may be private or restricted.",
            };
          } else if (
            apiError.status === 404 ||
            (apiError.result &&
              apiError.result.error &&
              apiError.result.error.status === "NOT_FOUND")
          ) {
            slideError.value = {
              type: "notfound",
              title: "Presentation Not Found",
              message:
                "This presentation could not be found. It may have been deleted or the URL is incorrect.",
            };
          } else {
            slideError.value = {
              type: "general",
              title: "Error Loading Presentation",
              message:
                "There was an error loading the presentation. Please try again later.",
            };
          }
        } finally {
          isLoading.value = false;
        }
      };

      requestAuth(tokenCallback);
    } else {
      // If Google API initialization failed
      slideError.value = {
        type: "api_init",
        title: "Google API Error",
        message:
          "Could not initialize Google APIs. Please check your connection and try again.",
      };
      isLoading.value = false;
    }
  } catch (error) {
    console.error("Error in fetchSlideInfo:", error);
    slideError.value = {
      type: "unknown",
      title: "Unknown Error",
      message: "An unexpected error occurred. Please try again later.",
    };
    isLoading.value = false;
  }
};

const goToSelectStep = () => {
  if (!isValidUrl.value) return;

  currentStep.value = "select";

  const normalizedTabValue = normalizeTabValue(props.activeTabIndex);
  selectedTab.value = normalizedTabValue;

  if (slides.value.length === 0) {
    const slideId = getSlideId(slideUrl.value);
    if (slideId) {
      fetchSlideInfo(slideId);
    }
  }
};

const isSlideSelected = (index) => {
  return selectedSlides.value.includes(index);
};

const toggleSlideSelection = (index) => {
  if (isSlideSelected(index)) {
    selectedSlides.value = selectedSlides.value.filter((i) => i !== index);
  } else {
    selectedSlides.value.push(index);
  }
};

const toggleAllSlides = () => {
  if (allSlidesSelected.value) {
    selectedSlides.value = [];
  } else {
    selectedSlides.value = slides.value.map((_, index) => index);
  }
};

const setActiveSlide = (index) => {
  activeSlide.value = index;
};

const handleSlideItemClick = (index) => {
  setActiveSlide(index);
  toggleSlideSelection(index);
};

const insertSelectedSlides = () => {
  if (selectedTab.value !== null) {
    selectedSlidesPerTab.value[selectedTab.value] = [...selectedSlides.value];
  }

  const hasAnySelections = Object.values(selectedSlidesPerTab.value).some(
    (selections) => selections.length > 0
  );
  if (!hasAnySelections) {
    alert("Please select at least one slide in any tab to insert");
    return;
  }

  const slideId = getSlideId(slideUrl.value);
  if (!slideId) return;

  // Prepare selections by tab
  const slideSelectionsByTab = {};

  // Process each tab's selections
  Object.entries(selectedSlidesPerTab.value).forEach(
    ([tabValue, selectedIndices]) => {
      if (selectedIndices.length === 0) return; // Skip tabs with no selections

      // Convert indices to slide data
      const tabSlides = selectedIndices.map((index) => {
        const slide = slides.value[index];
        return {
          index: slide.index,
          objectId: slide.objectId,
          thumbnailUrl: slide.thumbnailUrl,
          notes: slide.notes,
          title: slide.title,
        };
      });

      // Store the tab's slides - tabValue is already a string from Object.entries
      slideSelectionsByTab[tabValue] = tabSlides;
    }
  );

  // Emit the insert event with all necessary data
  emit("insert", {
    type: "slideDeck",
    url: slideUrl.value,
    embedUrl: embedUrl.value,
    slideId: slideId,
    slideSelectionsByTab: slideSelectionsByTab,
    slideCount: slideInfo.value?.slideCount || slides.value.length,
    lastUpdated: slideInfo.value?.lastUpdated || null,
    slideUploadCode: slideUploadCode.value,
  });

  close();
};

// Validate the URL
const validateUrl = async () => {
  if (!slideUrl.value) {
    urlError.value = "";
    isValidUrl.value = false;
    slideInfo.value = null;
    thumbnailUrl.value = null;
    slideError.value = null;
    return;
  }

  try {
    const url = new URL(slideUrl.value);
    if (
      url.hostname.includes("google.com") &&
      (url.pathname.includes("/presentation/") || url.pathname.includes("/d/"))
    ) {
      urlError.value = "";
      isValidUrl.value = true;
      slideError.value = null;

      const slideId = getSlideId(slideUrl.value);
      if (slideId) {
        try {
          await fetchSlideInfo(slideId);
        } catch (error) {
          console.error("Error fetching slide info:", error);
        }
      }
    } else {
      urlError.value = "Please enter a valid Google Slides URL";
      isValidUrl.value = false;
      slideInfo.value = null;
      thumbnailUrl.value = null;
      slideError.value = null;
    }
  } catch (e) {
    urlError.value = "Please enter a valid URL";
    isValidUrl.value = false;
    slideInfo.value = null;
    thumbnailUrl.value = null;
    slideError.value = null;
  }
};

// Close the modal
const close = () => {
  slideUrl.value = "";
  urlError.value = "";
  isValidUrl.value = false;
  slideInfo.value = null;
  thumbnailUrl.value = null;
  isLoading.value = false;
  slides.value = [];
  selectedSlides.value = [];
  activeSlide.value = null;
  currentStep.value = "input";
  slideError.value = null;
  emit("close");
};

watch(
  () => props.show,
  (newVal) => {
    if (newVal) {
      // Set the slide URL if we're changing an existing slide deck
      if (props.isChanging && props.currentSlideUrl) {
        slideUrl.value = props.currentSlideUrl;
        validateUrl();
      }

      // When modal opens, attempt to load Google API only if not already initialized
      if (!gapiInited.value || !gisInited.value) {
        loadGoogleApis().catch((error) => {
          console.error("Error loading Google APIs:", error);
        });
      }

      // Set selected tab to active tab
      const normalizedTabValue = normalizeTabValue(props.activeTabIndex);
      selectedTab.value = normalizedTabValue;
    } else {
      close();
    }
  }
);

watch(
  slides,
  (newSlides) => {
    if (newSlides.length > 0 && selectedSlides.value.length === 0) {
      selectedSlides.value = newSlides.map((_, index) => index);
      if (activeSlide.value === null) {
        activeSlide.value = 0;
      }
    }
  },
  { deep: true }
);

onMounted(() => {
  loadGoogleApis().catch((error) => {
    console.error("Error loading Google APIs on mount:", error);
  });

  const normalizedTabValue = normalizeTabValue(props.activeTabIndex);
  selectedTab.value = normalizedTabValue;
});
</script>

<style scoped>
/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background-color: white;
  padding: 25px;
  border-radius: 8px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
  width: 90%;
  max-width: 800px;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-content.slides-view {
  max-width: 900px;
  width: 95%;
  height: 85vh;
  max-height: 900px;
  display: flex;
  flex-direction: column;
}

.modal-content h3 {
  margin-top: 0;
  margin-bottom: 20px;
  font-size: 1.25em;
  color: #343a40;
}

.modal-body {
  margin-bottom: 20px;
}

.input-group {
  margin-bottom: 1.5rem;
}

.input-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  text-align: center;
}

.input-group input {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 1rem;
}

.input-with-button {
  display: flex;
  gap: 8px;
}

.input-with-button input {
  flex: 1;
}

.browse-button {
  padding: 0.75rem 1rem;
  background-color: #f8f9fa;
  border: 1px solid #ced4da;
  border-radius: 4px;
  cursor: pointer;
  white-space: nowrap;
}

.browse-button:hover {
  background-color: #e9ecef;
}

.error-message {
  color: #dc3545;
  font-size: 0.875rem;
  margin-top: 0.5rem;
}

.preview-container {
  margin-top: 1rem;
  margin-bottom: 1.5rem;
}

.preview-container h4 {
  margin-bottom: 0.75rem;
}

.slide-preview {
  border: 1px solid #ced4da;
  border-radius: 4px;
  overflow: hidden;
  background-color: #f8f9fa;
  margin-bottom: 1rem;
}

.slide-selection-description {
  font-size: 0.875rem;
  color: #6c757d;
}

.thumbnail-preview {
  border: 1px solid #ced4da;
  border-radius: 4px;
  overflow: hidden;
  background-color: #f8f9fa;
  margin-bottom: 1rem;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 10px;
}

.thumbnail-preview img {
  max-width: 100%;
  max-height: 300px;
  object-fit: contain;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.slide-info {
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  padding: 0.75rem;
  font-size: 0.875rem;
}

.slide-info p {
  margin: 0.25rem 0;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  background-color: #f8f9fa;
  border-radius: 4px;
  border: 1px solid #e9ecef;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-radius: 50%;
  border-top-color: var(--primary-blue, #0080ff);
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 1.5rem;
}

.btn-cancel {
  padding: 0.5rem 1rem;
  background-color: #f8f9fa;
  border: 1px solid #ced4da;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
}

.btn-next,
.btn-insert {
  padding: 0.5rem 1rem;
  background-color: var(--primary-blue, #0080ff);
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
}

.btn-back {
  padding: 0.5rem 1rem;
  background-color: #e9ecef;
  border: 1px solid #ced4da;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
}

.btn-next:disabled,
.btn-insert:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

/* Slides Selection View Styles */
.slides-selection-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  flex: 1;
}

.slides-layout {
  display: flex;
  flex-direction: column;
  gap: 20px;
  height: 100%;
  min-height: 0;
  overflow: hidden;
}

.slides-list {
  border-radius: 6px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.selection-header {
  padding: 12px;
  border-bottom: 1px solid #e9ecef;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.select-all-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 500;
  cursor: pointer;
}

.select-all-label input {
  cursor: pointer;
  width: 16px;
  height: 16px;
}

.tab-selector {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.tab-selector-item {
  padding: 8px;
  color: #808183;
  cursor: pointer;
  position: relative;
  display: flex;
  align-items: center;
  gap: 5px;
}

.tab-selector-item.active {
  color: var(--primary-blue, #0080ff);
  border-bottom: 2px solid var(--primary-blue, #0080ff);
}

.tab-selection-count {
  font-size: 0.8em;
  color: #666;
  padding: 2px 6px;
  background-color: #f0f0f0;
  border-radius: 10px;
  font-weight: 500;
}

.tab-selector-item.active .tab-selection-count {
  background-color: var(--primary-blue, #0080ff);
  color: white;
}

.slides-list-content {
  flex: 1;
  overflow-y: auto;
  padding: 10px;
}

.slide-item {
  display: flex;
  align-items: stretch;
  padding: 8px;
  border-top: 1px solid #e9ecef;
  margin-bottom: 10px;
  cursor: pointer;
  background-color: #fff;
  gap: 10px;
  transition: all 0.2s ease;
}

.slide-item-content {
  display: flex;
  flex-direction: column;
  align-items: start;
  gap: 10px;
  width: 20%;
  max-width: 200px;
  padding-right: 10px;
  border-right: 1px solid #e9ecef;
}

.slide-item:hover {
  background-color: #f8f9fa;
  border-color: #ced4da;
}

.slide-item.selected {
  background-color: rgba(0, 128, 255, 0.05);
}

.slide-item.active {
  box-shadow: 0 0 0 2px rgba(0, 128, 255, 0.3);
}

.slide-checkbox {
  display: flex;
  align-items: center;
}

.slide-checkbox input {
  width: 16px;
  height: 16px;
  cursor: pointer;
}

.slide-number {
  min-width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  font-weight: 500;
  color: #495057;
}

.slide-thumbnail {
  flex: 1;
  height: 60px;
  overflow: hidden;
  border-radius: 3px;
  background-color: #f8f9fa;
}

.slide-thumbnail img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.slide-detail {
  flex: 1;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.slide-preview-large {
  padding: 20px;
  background-color: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
  max-height: 40%;
}

.slide-preview-large img {
  max-width: 100%;
  max-height: 300px;
  object-fit: contain;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border-radius: 4px;
}

.slide-notes-container {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  padding: 20px;
  box-sizing: border-box;
  cursor: pointer;
  border-radius: 6px;
}

.slide-notes-container h4 {
  margin-top: 0;
  margin-bottom: 10px;
  font-size: 1rem;
}

.slide-notes-container textarea {
  display: block;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  flex: 0 0 auto;
  padding: 10px;
  border-radius: 4px;
  resize: none;
  font-family: inherit;
  font-size: 0.9rem;
  line-height: 1.5;
  cursor: text;
}

.slide-notes-select-gutter {
  flex: 1 1 auto;
  min-height: 28px;
}

@media (max-width: 768px) {
  .slides-layout {
    flex-direction: column;
  }

  .slides-list {
    width: 100%;
    max-width: 100%;
  }

  .slide-detail {
    max-height: 60%;
  }

  .slide-preview-large {
    min-height: 200px;
  }

  .modal-content.slides-view {
    height: 95vh;
    max-height: none;
    padding: 15px;
  }
}

/* Error container styles */
.error-container {
  display: flex;
  background-color: #fff3f3;
  border: 1px solid #ffcccb;
  border-radius: 8px;
  padding: 16px;
  margin: 20px 0;
  gap: 16px;
  align-items: flex-start;
}

.error-icon {
  color: #e74c3c;
  flex-shrink: 0;
}

.error-content {
  flex: 1;
}

.error-content h4 {
  margin-top: 0;
  margin-bottom: 8px;
  color: #e74c3c;
  font-weight: 600;
}

.error-content p {
  margin: 0 0 8px;
  color: #333;
  font-size: 0.9rem;
  line-height: 1.5;
}

.error-tip {
  background-color: #fffaeb;
  border-left: 3px solid #f7d794;
  padding: 8px 12px;
  margin-top: 8px;
  font-size: 0.85rem;
}
</style>
